import PropTypes from 'prop-types';
import { Box, Card, Chip, CircularProgress, Tooltip, Typography } from '@material-ui/core';
import GroupsIcon from '@mui/icons-material/GroupsRounded';
import EventIcon from '@mui/icons-material/EventRounded';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlineRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import StarRating from './StarRating';
import { alpha } from '@mui/material/styles';
import { grey, greyAlpha } from '../../../../theme/tones';
import {
  formatDateRange,
  formatStyle,
  scoreColor,
  scoreLabel,
  sessionStatusStyle
} from './constants';

/** Anneau de score : la jauge se lit avant même le chiffre. */
const ScoreRing = ({ value, size = 64 }) => {
  const color = scoreColor(value);
  const pct = value === null || value === undefined ? 0 : Math.max(0, Math.min(100, value));

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        background: `conic-gradient(${color} ${pct * 3.6}deg, ${greyAlpha(400, 0.18)} 0deg)`
      }}
    >
      <Box
        sx={{
          width: size - 10,
          height: size - 10,
          borderRadius: '50%',
          bgcolor: 'background.paper',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: size > 56 ? '1rem' : '0.85rem', color, lineHeight: 1 }}>
          {value === null || value === undefined ? '—' : `${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

ScoreRing.propTypes = { value: PropTypes.number, size: PropTypes.number };

const MetaLine = ({ icon, children, muted }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box sx={{ color: 'text.disabled', display: 'flex', fontSize: 15 }}>{icon}</Box>
    <Typography
      noWrap
      sx={{
        fontSize: '0.75rem',
        color: muted ? 'text.disabled' : 'text.secondary',
        fontWeight: 600,
        fontStyle: muted ? 'italic' : 'normal'
      }}
    >
      {children}
    </Typography>
  </Box>
);

MetaLine.defaultProps = { muted: false };

MetaLine.propTypes = { icon: PropTypes.node, children: PropTypes.node, muted: PropTypes.bool };

/**
 * Une carte = une session. C'est l'unité de lecture de la vue d'ensemble :
 * le détail des réponses individuelles n'est chargé qu'au clic.
 *
 * `cta` permet de réemployer la carte pour une autre action que la lecture des
 * réponses — la page des comptes rendus s'en sert pour télécharger le document
 * de la session, d'où aussi `busy` (génération côté serveur) et `showScore`
 * (une moyenne tous types confondus ne veut rien dire).
 */
const SessionResponseCard = ({ group, accent, showRating, showScore, onOpen, cta, busy }) => {
  const fmt = formatStyle(group.format);
  const status = sessionStatusStyle(group.session_status);
  const color = scoreColor(group.average);
  const place = group.meeting_town || group.meeting_place_name;
  // Le formateur vient du champ « Formateur » de la fiche session, souvent vide
  // sur les sessions anciennes : on le dit plutôt que de masquer la ligne.
  const trainer = (group.trainer || '').trim();
  const ctaLabel = cta?.label || 'Détail';
  const ctaIcon = cta?.icon || <ArrowForwardIcon sx={{ fontSize: 14 }} />;

  return (
    <Card
      onClick={() => { if (!busy) onOpen(group); }}
      sx={{
        p: 2.25,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        borderRadius: '20px',
        border: `1px solid ${grey(100)}`,
        boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
        cursor: busy ? 'progress' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: accent,
          boxShadow: `0 12px 28px -12px ${accent}66`
        },
        '&:hover .card-cta': { opacity: 1, transform: 'translateX(0)' }
      }}
    >
      {/* Liseré de score sur la tranche gauche */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: showScore ? color : accent }} />

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', pl: 0.5 }}>
        <Chip
          label={group.format || 'Format inconnu'}
          size="small"
          sx={{ bgcolor: fmt.bg, color: fmt.color, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
        />
        {group.session_status && (
          <Chip
            label={group.session_status}
            size="small"
            sx={{ bgcolor: status.bg, color: status.color, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: 'text.disabled', letterSpacing: '0.04em' }}>
          SESSION {group.session_number ?? '—'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pl: 0.5 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Tooltip title={group.formation_name || ''}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '0.92rem',
                color: 'text.primary',
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {group.formation_name || 'Formation inconnue'}
            </Typography>
          </Tooltip>
          {group.program_number && (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 700, mt: 0.5 }}>
              N° {group.program_number}
            </Typography>
          )}
        </Box>
        {showScore && (
          <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
            <ScoreRing value={group.average} />
            {showRating && <StarRating percent={group.average} size="0.74rem" color={color} iconSize="0.92rem" />}
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {scoreLabel(group.average)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
        <MetaLine icon={<EventIcon fontSize="inherit" />}>
          {formatDateRange(group.start_date, group.end_date)}
        </MetaLine>
        {place && <MetaLine icon={<PlaceIcon fontSize="inherit" />}>{place}</MetaLine>}
        <Tooltip title={trainer || "Aucun formateur renseigné sur la fiche session"}>
          <Box sx={{ minWidth: 0 }}>
            <MetaLine icon={<PersonIcon fontSize="inherit" />} muted={!trainer}>
              {trainer || 'Pas encore inséré'}
            </MetaLine>
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 0.5 }}>
        {group.professions.slice(0, 3).map((p) => (
          <Chip
            key={p}
            label={p}
            size="small"
            sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', height: 19 }}
          />
        ))}
        {group.professions.length > 3 && (
          <Tooltip title={group.professions.slice(3).join(', ')}>
            <Chip
              label={`+${group.professions.length - 3}`}
              size="small"
              sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', height: 19 }}
            />
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pt: 1.5,
          pl: 0.5,
          borderTop: `1px dashed ${grey(200)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <GroupsIcon sx={{ fontSize: 16, color: accent }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'text.primary' }}>
            {group.responses}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600 }}>
            {group.responses > 1 ? 'réponses' : 'réponse'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: grey(300) }}>•</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600 }}>
          {group.learners} apprenant{group.learners > 1 ? 's' : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {/* Une action qui n'existe qu'au survol est invisible au tactile : quand
            la carte porte un téléchargement (`cta.alwaysVisible`), l'icône
            reste là au repos et c'est le survol qui l'accentue. */}
        <Box
          className="card-cta"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
            color: accent,
            fontWeight: 800,
            fontSize: '0.72rem',
            whiteSpace: 'nowrap',
            opacity: cta?.alwaysVisible ? 0.75 : 0,
            transform: cta?.alwaysVisible ? 'none' : 'translateX(-6px)',
            transition: 'opacity .18s ease, transform .18s ease'
          }}
        >
          {busy ? (
            <>
              Préparation… <CircularProgress size={13} sx={{ color: accent, ml: 0.2 }} />
            </>
          ) : (
            <>
              {ctaLabel} {ctaIcon}
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
};

SessionResponseCard.defaultProps = { showScore: true, cta: null, busy: false };

SessionResponseCard.propTypes = {
  group: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  showRating: PropTypes.bool,
  /** À false, la carte ne montre plus l'anneau de score (moyenne non pertinente). */
  showScore: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  /** { label, icon, alwaysVisible } — remplace l'appel à l'action « Détail ». */
  cta: PropTypes.object,
  /** Action en cours pour cette carte : clic neutralisé, indicateur visible. */
  busy: PropTypes.bool
};

export default SessionResponseCard;
export { ScoreRing };
